import React, { useRef, useState, useEffect } from "react";
import { Product1, Product2, Product3, Product4 } from "../../Assets.tsx";
import { useHistory } from "react-router-dom";

/* Colours: text #4e5356 | buttons #2596be | headings #4573a6
   Fonts  : Ubuntu Bold (headings) / Open Sans Regular (body)
   Layout : 4 cards in a single row.
   Hover  : spotlight + 3D tilt + top bar + button fill. Icons keep ORIGINAL colours. */

type Product = { image: string; name: string; description: string };

const cardData: Product[] = [
  { image: Product1, name: "Milk App", description: "Milk Dairy offers fresh, farm-to-home dairy products delivered daily to your doorstep." },
  { image: Product2, name: "Flexa", description: "Platform to list and book flex hoardings easily without manual hassle." },
  { image: Product3, name: "Matrimonial App", description: "Connect with verified profiles for meaningful relationships." },
  { image: Product4, name: "Ecommerce App", description: "Seamless shopping with delivery tracking and secure payments." },
];

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400&family=Ubuntu:wght@700&display=swap");
.op{--text:#4e5356;--head:#4573a6;--btn:#2596be;--tint:#eef6fa;--line:#d3e4ed;
  position:relative;overflow:hidden;padding:90px 20px 100px;text-align:center;font-family:"Open Sans",sans-serif;font-weight:400;color:var(--text);
background: #ffffff;
.ap::before {
  content: none;
}
  .op>*{position:relative}

/* header drops from the top */
.op-head{animation:apDrop .9s cubic-bezier(.2,.8,.2,1) backwards}
.op-chip{display:inline-block;margin-bottom:16px;padding:6px 18px;border-radius:50px;background:#fff;border:1px solid var(--line);
  color:var(--head);font-size:12px;letter-spacing:2px;text-transform:uppercase}
.op-head h2{font-family:"Ubuntu",sans-serif;font-weight:700;font-size:42px;line-height:1.15;margin:0 0 14px;color:var(--head);cursor:default;transition:color .35s,letter-spacing .35s}
.op-head h2:hover{color:var(--btn);letter-spacing:.6px}
.op-sub{display:flex;align-items:center;justify-content:center;gap:12px;margin-bottom:64px}
.op-sub i{display:block;height:2px;width:40px;border-radius:2px;background:var(--btn)}.op-sub i:last-child{background:var(--head)}
.op-sub p{margin:0;font-size:15px;color:var(--text)}

/* 4 cards, single row */
.op-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:22px;max-width:1240px;margin:0 auto}

/* ---------- CARD ---------- */
.op-card{--mx:50%;--my:50%;--rx:0deg;--ry:0deg;
  position:relative;overflow:hidden;cursor:pointer;text-align:center;padding:38px 22px 28px;border-radius:24px;
  background:#fff;border:1px solid var(--line);box-shadow:0 18px 36px -26px rgba(69,115,166,.55);
  transform:perspective(900px) rotateX(var(--rx)) rotateY(var(--ry));
  transition:transform .18s ease-out,box-shadow .4s,border-color .4s;
  display:flex;flex-direction:column;align-items:center}
.op-card:hover{border-color:transparent;box-shadow:0 30px 50px -22px rgba(37,150,190,.7)}

/* entrance: cards rise one after another */
.op-grid:not(.in) .op-card{opacity:0}
.op-grid.in .op-card{animation:opRise .8s cubic-bezier(.2,.8,.2,1) backwards;animation-delay:calc(var(--i) * .12s)}
@keyframes opRise{from{opacity:0;transform:translateY(60px)}}

/* spotlight following the mouse */
.op-glow{position:absolute;inset:0;pointer-events:none;opacity:0;transition:opacity .35s;
  background:radial-gradient(240px circle at var(--mx) var(--my),rgba(37,150,190,.16),rgba(69,115,166,.05) 45%,transparent 70%)}
.op-card:hover .op-glow{opacity:1}

/* top bar fills left to right */
.op-top{position:absolute;top:0;left:0;width:100%;height:5px;transform:scaleX(0);transform-origin:left;
  background:linear-gradient(90deg,var(--head),var(--btn));transition:transform .55s cubic-bezier(.2,.8,.2,1)}
.op-card:hover .op-top{transform:scaleX(1)}

/* outline number watermark (small, top-right corner, moves slightly up-right so it never overlaps the icon) */
.op-num{position:absolute;top:10px;right:14px;font-size:54px;font-weight:800;line-height:1;pointer-events:none;color:transparent;
  -webkit-text-stroke:1.5px rgba(69,115,166,.14);transition:transform .5s cubic-bezier(.2,.8,.2,1),-webkit-text-stroke-color .4s}
.op-card:hover .op-num{transform:translate(4px,-4px);-webkit-text-stroke-color:rgba(37,150,190,.4)}

/* icon tile: no ring, no circle. Only a gentle lift, icon keeps its colours */
.op-icon{position:relative;width:104px;height:104px;margin-bottom:22px;border-radius:28px;background:var(--tint);
  display:flex;align-items:center;justify-content:center;
  transition:background .4s,box-shadow .4s,transform .5s cubic-bezier(.3,1.4,.5,1)}
.op-icon img{position:relative;height:60px;width:60px;object-fit:contain;transition:transform .5s cubic-bezier(.3,1.4,.5,1)}
.op-card:hover .op-icon{background:#fff;transform:translateY(-6px);
  box-shadow:0 10px 22px -8px rgba(0,0,0,.35)}
.op-card:hover .op-icon img{transform:translateY(3px) scale(1.08)}

.op-card h3{position:relative;display:inline-block;margin:0 0 12px;color:var(--head);font-size:20px;font-family:"Ubuntu",sans-serif;font-weight:700;letter-spacing:.6px}
.op-card h3::after{content:"";position:absolute;left:0;bottom:-5px;height:2px;width:0;background:var(--btn);transition:width .45s cubic-bezier(.2,.8,.2,1)}
.op-card:hover h3::after{width:100%}
.op-card p{position:relative;margin:0 0 22px;font-size:14.5px;line-height:1.75;color:var(--text);min-height:72px}

/* button: navy fill slides in from the left */
.op-btn{position:relative;overflow:hidden;margin-top:auto;display:inline-flex;align-items:center;gap:8px;padding:11px 24px;border:none;border-radius:50px;
  background:var(--tint);color:var(--head);font-weight:600;font-size:14px;cursor:pointer;z-index:0;transition:color .35s}
.op-btn::before{content:"";position:absolute;inset:0;z-index:-1;background:linear-gradient(90deg,var(--head),var(--btn));
  transform:translateX(-101%);transition:transform .45s cubic-bezier(.2,.8,.2,1)}
.op-btn span{display:inline-block;transition:transform .35s}
.op-card:hover .op-btn{color:#fff}
.op-card:hover .op-btn::before{transform:none}
.op-btn:hover span{transform:translateX(5px)}

/* ---------- RESPONSIVE (only wraps on small screens) ---------- */
@media(max-width:1100px){.op-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media(max-width:600px){
  .op{padding:70px 16px 80px}.op-head h2{font-size:30px}.op-sub{margin-bottom:44px}
  .op-grid{grid-template-columns:1fr}
}
@media(prefers-reduced-motion:reduce){
  .op *,.op *::before,.op *::after{animation:none!important;transition:none!important}
  .op-grid .op-card{opacity:1}
}
`;

const Card = ({ item, index }: { item: Product; index: number }) => {
  const history = useHistory();
  const ref = useRef<HTMLDivElement | null>(null);

  // spotlight follows mouse + gentle 3D tilt
  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    el.style.setProperty("--mx", x + "px");
    el.style.setProperty("--my", y + "px");
    el.style.setProperty("--ry", ((x / r.width - 0.5) * 8).toFixed(2) + "deg");
    el.style.setProperty("--rx", ((0.5 - y / r.height) * 8).toFixed(2) + "deg");
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  };

  return (
    <div
      className="op-card"
      ref={ref}
      style={{ ["--i" as any]: index }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <div className="op-top" />
      <div className="op-glow" />
      <span className="op-num">{String(index + 1).padStart(2, "0")}</span>

      <div className="op-icon">
        <img src={item.image} alt={item.name} />
      </div>

      <h3>{item.name}</h3>
      <p>{item.description}</p>

      <button className="op-btn" onClick={() => history.push("/contact")}>
        Explore <span>→</span>
      </button>
    </div>
  );
};

const OurProducts = () => {
  const history = useHistory();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    if (gridRef.current) observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="op">
      <style>{STYLES}</style>

<div className="op-head">
        <div className="op-chip">What We Built</div>
        <h2>Our Products</h2>
        <div className="op-sub">
          <i />
          <p>With ATUM's product development services, you get more than just a solution.</p>
          <i />
        </div>
      </div>

      <div className={"op-grid" + (inView ? " in" : "")} ref={gridRef}>
        {cardData.map((item, index) => (
          <Card key={index} item={item} index={index} />
        ))}
      </div>
    </div>
  );
};

export default OurProducts;